/**
 * §P.11 · El catálogo: lo que el club vende.
 *
 * ## El `DELETE` no borra, así que aquí no hay ningún botón que diga «borrar»
 *
 * Lo dice el contrato: «desactiva, no borra». `material_requests.catalog_item_id` es
 * `ON DELETE RESTRICT`, y con razón — una solicitud cuyo artículo desapareció deja de
 * decir qué pidió la familia.
 *
 * La primera versión de esta pantalla tenía dos botones, «Dejar de venderlo» (`PATCH
 * active:false`) y «Borrarlo» (`DELETE`), como si fueran dos cosas distintas. No lo
 * son: las dos desactivan. Y el segundo prometía algo que no pasa, que es peor que
 * faltar. Visto mirándolo en el navegador, no leyendo el código.
 *
 * Lo que sí distingue al `DELETE` es que **se niega si hay solicitudes sin
 * entregar** (`409 item_has_pending_requests`): es el verbo con red. Así que retirar
 * usa `DELETE` y volver a ponerlo a la venta usa el `PATCH`. Un botón por cosa, y el
 * que protege es el que se ofrece.
 *
 * ## Las tallas se recortan antes de mandarlas
 *
 * `/summary` agrupa por la cadena exacta. Una «130 » con un espacio al final parte el
 * pedido en dos líneas y el proveedor recibe dos pedidos de uno en lugar de uno de
 * dos — que es justo el error que esta función existe para eliminar.
 *
 * ## El precio se escribe en euros y viaja en céntimos
 *
 * Como todo el dinero del sistema. `aEuros` rellena el campo y no lleva separador de
 * miles a propósito: rompería su propio lector.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta, euros } from '@/shared/ui/formato';
import { aEuros } from '@/club/cuotas/tipos';
import {
  CATEGORIAS,
  etiquetaDeCategoria,
  type ArticuloDelCatalogo,
  type CategoriaDeMaterial,
} from '@/club/material/tipos';

interface Campos {
  name: string;
  description: string;
  category: string;
  precio: string;
  tallas: string;
  on_demand: boolean;
}

const VACIO: Campos = {
  name: '',
  description: '',
  category: 'dobok',
  precio: '',
  tallas: '',
  on_demand: true,
};

const desde = (a: ArticuloDelCatalogo): Campos => ({
  name: a.name,
  description: a.description ?? '',
  category: a.category ?? '',
  precio: aEuros(a.price_cents),
  tallas: a.sizes.join(', '),
  on_demand: a.on_demand,
});

/**
 * «120, 130, 140» → `['120','130','140']`.
 *
 * Recorta y quita las vacías: una coma de más no puede convertirse en una talla sin
 * nombre que luego salga como una línea propia en el pedido al proveedor.
 */
const aTallas = (texto: string): string[] =>
  texto
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t !== '');

const aCentimos = (precio: string): number =>
  Math.round(Number(precio.replace(',', '.')) * 100);

/**
 * Qué formulario hay abierto.
 *
 * Un tipo propio y no `string | 'nuevo'`: los dos son cadenas, así que TypeScript
 * colapsa la unión y el literal deja de significar nada — lo dice el linter. Con esto
 * el compilador obliga a distinguir «estoy creando» de «estoy editando el id tal»,
 * que es justo lo que decide si la llamada es un `POST` o un `PATCH`.
 */
type Edicion = { modo: 'nuevo' } | { modo: 'editar'; id: string } | null;

export function Catalogo({
  club,
  puedeEditar,
}: {
  club: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [verRetirados, setVerRetirados] = useState(false);
  const [editando, setEditando] = useState<Edicion>(null);
  const [campos, setCampos] = useState<Campos>(VACIO);
  const [problema, setProblema] = useState<Problema | null>(null);

  const catalogo = useQuery({
    queryKey: ['catalogo', club, verRetirados],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).catalog.get({
        query: verRetirados ? { include_inactive: true } : {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as ArticuloDelCatalogo[];
    },
  });

  const refrescar = () => {
    setEditando(null);
    setProblema(null);
    void cache.invalidateQueries({ queryKey: ['catalogo', club] });
  };

  const guardar = useMutation({
    mutationFn: async ({ edicion, c }: { edicion: Edicion; c: Campos }) => {
      const cuerpo = {
        name: c.name.trim(),
        price_cents: aCentimos(c.precio),
        description: c.description.trim() === '' ? null : c.description.trim(),
        category:
          c.category === '' ? null : (c.category as CategoriaDeMaterial),
        sizes: aTallas(c.tallas),
        on_demand: c.on_demand,
      };

      const r =
        edicion?.modo === 'editar'
          ? await api.v1
              .clubs({ club })
              .catalog({ id: edicion.id })
              .patch(cuerpo)
          : await api.v1.clubs({ club }).catalog.post(cuerpo);

      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: refrescar,
    onError: (e) => setProblema(aProblema(e)),
  });

  /** Volver a ponerlo a la venta: es lo único que hace el `PATCH` de `active`. */
  const reactivar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1
        .clubs({ club })
        .catalog({ id })
        .patch({ active: true });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: refrescar,
    onError: (e) => setProblema(aProblema(e)),
  });

  /** Retirarlo. **No borra**, y se niega si hay solicitudes sin entregar. */
  const retirar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1.clubs({ club }).catalog({ id }).delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: refrescar,
    onError: (e) => setProblema(aProblema(e)),
  });

  if (catalogo.isPending) {
    return <Cargando filas={4} etiqueta="Cargando el catálogo" />;
  }

  if (catalogo.isError || !catalogo.data) {
    return (
      <PanelError
        problema={aProblema(catalogo.error)}
        reintentar={() => void catalogo.refetch()}
      />
    );
  }

  const formulario = (edicion: Edicion) => (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        setProblema(null);
        guardar.mutate({ edicion, c: campos });
      }}
    >
      <div className="rejilla">
        <label>
          Qué es
          <input
            required
            maxLength={160}
            placeholder="Dobok de entrenamiento"
            value={campos.name}
            onChange={(e) => setCampos({ ...campos, name: e.target.value })}
          />
        </label>

        <label>
          Precio
          <input
            required
            inputMode="decimal"
            placeholder="45,00"
            value={campos.precio}
            onChange={(e) => setCampos({ ...campos, precio: e.target.value })}
          />
        </label>

        <label>
          Categoría
          <select
            value={campos.category}
            onChange={(e) => setCampos({ ...campos, category: e.target.value })}
          >
            <option value="">Sin categoría</option>
            {CATEGORIAS.map((c) => (
              <option key={c.id} value={c.id}>
                {c.etiqueta}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label>
        Tallas, separadas por comas
        <input
          maxLength={400}
          placeholder="120, 130, 140, 150"
          value={campos.tallas}
          onChange={(e) => setCampos({ ...campos, tallas: e.target.value })}
        />
      </label>

      <label>
        Descripción
        <textarea
          rows={2}
          maxLength={1000}
          placeholder="El de diario. Cuello blanco para cinturones de color."
          value={campos.description}
          onChange={(e) =>
            setCampos({ ...campos, description: e.target.value })
          }
        />
      </label>

      <div className="rejilla">
        <label className="opcion">
          <input
            type="checkbox"
            checked={campos.on_demand}
            onChange={(e) =>
              setCampos({ ...campos, on_demand: e.target.checked })
            }
          />
          Hay que pedirlo al proveedor (no lo tengo en el club)
        </label>
      </div>

      <div className="acciones">
        <button
          type="submit"
          className="btn-primario"
          disabled={guardar.isPending}
        >
          {guardar.isPending ? 'Guardando…' : 'Guardar'}
        </button>
        <button
          type="button"
          className="btn-plano"
          onClick={() => {
            setEditando(null);
            setProblema(null);
          }}
        >
          Dejarlo
        </button>
      </div>
    </form>
  );

  return (
    <section>
      <header className="titulo-fila">
        <h1>Catálogo</h1>
        <div className="acciones">
          <button
            type="button"
            className={verRetirados ? 'pestaña-activa' : 'btn-plano'}
            onClick={() => setVerRetirados(!verRetirados)}
          >
            {verRetirados ? 'Solo lo de esta temporada' : 'Ver lo retirado'}
          </button>
          {puedeEditar && editando?.modo !== 'nuevo' && (
            <button
              type="button"
              className="btn-primario"
              onClick={() => {
                setCampos(VACIO);
                setEditando({ modo: 'nuevo' });
              }}
            >
              Añadir artículo
            </button>
          )}
        </div>
      </header>

      <p className="tenue">
        Lo que vendes a las familias, con sus tallas y su precio.{' '}
        <strong>Kiap no cobra nada de esto</strong>: el pago sigue ocurriendo
        donde ocurre hoy, y lo que aporta esta pantalla es la lista.
      </p>

      {problema && <PanelError problema={problema} />}

      {editando?.modo === 'nuevo' && formulario(editando)}

      {catalogo.data.length === 0 ? (
        <Vacio
          titulo="Todavía no vendes nada por aquí"
          explicacion="Añade el dobok y el cinturón, que es lo que pide todo el mundo en septiembre. Con eso ya puedes abrir un pedido agrupado."
        />
      ) : (
        <ul className="papeles">
          {catalogo.data.map((a) => (
            <li key={a.id}>
              {editando?.modo === 'editar' && editando.id === a.id ? (
                formulario(editando)
              ) : (
                <>
                  <div className="aviso-cabeza">
                    <strong>{a.name}</strong>
                    <span className="pastilla pastilla-left">
                      {etiquetaDeCategoria(a.category)}
                    </span>
                    {!a.active && (
                      <span className="pastilla pastilla-left">
                        Retirado de la venta
                      </span>
                    )}
                    {a.on_demand && (
                      <span className="pastilla pastilla-left">
                        Se pide al proveedor
                      </span>
                    )}
                  </div>

                  <p>
                    <strong>{euros(a.price_cents)}</strong>
                    {a.sizes.length > 0 && (
                      <span className="tenue"> · {a.sizes.join(' · ')}</span>
                    )}
                  </p>

                  {a.description && <p className="tenue">{a.description}</p>}

                  {a.pending_requests > 0 && (
                    <p className="tenue">
                      {cuenta(a.pending_requests, 'familia', 'familias')} lo{' '}
                      {a.pending_requests === 1 ? 'ha pedido' : 'han pedido'} y
                      está sin entregar.{' '}
                      {/*
                        Se dice aquí y no al pulsar: mientras haya pedidos vivos no
                        se puede retirar, y saberlo antes evita el `409`.
                      */}
                      <strong>
                        Hasta que {a.pending_requests === 1 ? 'se' : 'se'}{' '}
                        {a.pending_requests === 1 ? 'entregue' : 'entreguen'},
                        no se puede quitar de la venta.
                      </strong>
                    </p>
                  )}

                  {puedeEditar && (
                    <div className="acciones">
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() => {
                          setCampos(desde(a));
                          setEditando({ modo: 'editar', id: a.id });
                        }}
                      >
                        Editar
                      </button>

                      {a.active ? (
                        <button
                          type="button"
                          className="btn-plano"
                          disabled={retirar.isPending}
                          onClick={() => retirar.mutate(a.id)}
                          title={
                            a.pending_requests > 0
                              ? 'Tiene pedidos sin entregar: entrégalos o anúlalos primero'
                              : undefined
                          }
                        >
                          Dejar de venderlo
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-plano"
                          disabled={reactivar.isPending}
                          onClick={() => reactivar.mutate(a.id)}
                        >
                          Volver a venderlo
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
